import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../includes/ConfigFile';
import { toast } from 'react-toastify';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Pagination, Form } from 'react-bootstrap';

const EditChapters = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [storyTitle, setStoryTitle] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  // ✅ Delete modal states
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const itemsPerPage = 10;

  const fetchChapters = () => {
    axios.get(`${config.BASE_URL}api/getPostChapter/${postId}`)
      .then(res => {
        if (res.data.status) {
          setChapters(res.data.chapters);
          if (res.data.chapters.length > 0) {
            setStoryTitle(res.data.chapters[0].post_title || "");
          }
        } else {
          toast.error("No chapters found.");
        }
      })
      .catch(err => {
        toast.error("Error loading chapters.");
        console.error(err);
      });
  };

  useEffect(() => {
    if (postId) fetchChapters();
  }, [postId]);

  // ✅ Open delete confirmation modal
  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // ✅ Confirm delete action
  const confirmDelete = () => {
    axios.get(`${config.BASE_URL}/api/deletePostChapter/${deleteId}`)
      .then(res => {
        if (res.data.status) {
          toast.success("Chapter deleted successfully.");
          fetchChapters();
        } else {
          toast.error("Failed to delete chapter.");
        }
      })
      .catch(err => {
        toast.error("Error deleting chapter.");
        console.error(err);
      })
      .finally(() => {
        setShowDeleteModal(false);
        setDeleteId(null);
      });
  };

  const handleShowDescription = (chapter) => {
    setSelectedChapter(chapter);
    setShowModal(true);
  };

  const filteredChapters = chapters.filter((chapter) =>
    (chapter.chapter_no || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedChapters = filteredChapters.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredChapters.length / itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => (
    <Pagination className="justify-content-center mt-3">
      <Pagination.Prev
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      />
      {Array.from({ length: totalPages }, (_, i) => (
        <Pagination.Item
          key={i + 1}
          active={i + 1 === currentPage}
          onClick={() => handlePageChange(i + 1)}
        >
          {i + 1}
        </Pagination.Item>
      ))}
      <Pagination.Next
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      />
    </Pagination>
  );

  return (
    <div className="container my-5 p-4 bg-dark text-light rounded shadow-lg">
      <h2 className="text-center mb-4">
        📚 Chapter List {storyTitle && `of "${storyTitle}"`}
      </h2>

      <Form className="mb-4 d-flex justify-content-end">
        <Form.Control
          type="text"
          placeholder="🔍 Search Chapter Number..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          style={{
            maxWidth: "600px",
            width: "100%",
            borderRadius: "10px",
            padding: "10px 16px",
            fontSize: "1rem",
            backgroundColor: "#f8f9fa",
            border: "2px solid #ccc",
          }}
        />
      </Form>

      {filteredChapters.length === 0 ? (
        <div className="text-center mt-4">
          <h5 className="text-warning">
            ❌ No matching chapter found{searchTerm ? ` for "${searchTerm}"` : "."}
          </h5>
        </div>
      ) : (
        <div className="row">
          {paginatedChapters.map((chapter, index) => (
            <div className="col-md-6 col-lg-4 mb-4" key={chapter.id}>
              <div className="card bg-secondary text-white h-100 shadow rounded-4 border border-light">
                <div className="card-body d-flex flex-column justify-content-between">
                  <h5 className="card-title">
                    📘 {chapter.chapter_no || `Chapter ${indexOfFirstItem + index + 1}`}
                  </h5>
                  <p className="card-text text-light" style={{ fontSize: "0.9rem" }}>
                    {chapter.post_description
                      ? chapter.post_description.replace(/<[^>]+>/g, '').substring(0, 80) + "..."
                      : "No description available."}
                  </p>
                  <div className="mt-3 d-flex justify-content-between">
                    <Button
                      className="chapter-icon-btn me-2"
                      variant="outline-info"
                      size="sm"
                      onClick={() => navigate(`/editshowChapters/${chapter.id}`)}
                    >
                      ✏️
                    </Button>
                    <Button
                      className="chapter-icon-btn me-2"
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDelete(chapter.id)}
                    >
                      🗑
                    </Button>
                    <Button
                      className="chapter-icon-btn me-2"
                      variant="outline-light"
                      size="sm"
                      onClick={() => handleShowDescription(chapter)}
                    >
                      📖
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {renderPagination()}

      {/* 📖 View Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title className="text-dark">📖 Chapter Description</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-dark">
          <div dangerouslySetInnerHTML={{ __html: selectedChapter?.post_description || "No description available." }} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 🗑 Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">⚠️ Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-dark">
          Are you sure you want to delete this chapter? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            🗑 Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EditChapters;
